import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentPageComponent } from './document-page/document-page.component';
import { ApiService } from '../services/api.service';
import { DocumentsService } from './documents.service';
import { SearchService } from '../services/search.service';
import { Subject, combineLatest, of } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, map, startWith } from 'rxjs/operators';
import { PaginationComponent } from '../shared/pagination/pagination.component';

interface DocumentBlock {
  id: string;
  type: string;
  data: any;
}

interface DocData {
  time: number;
  version: string;
  blocks: DocumentBlock[];
}

interface Document {
  _id: string;
  title: string;
  description: string;
  module?: string;
  updatedAt?: string;
  thumbnailUrl?: string;
  projectId?: string;
  docData?: DocData;
  favoritedByCurrentUser?: boolean;
}

@Component({
  selector: 'app-document',
  standalone: true,
  imports: [CommonModule, DocumentPageComponent, PaginationComponent],
  templateUrl: './document.component.html',
  styleUrl: './document.component.scss',
})
export class DocumentComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);
  private documentsService = inject(DocumentsService);
  private searchService = inject(SearchService);

  @Output() documentViewStateChange = new EventEmitter<boolean>();

  documents: Document[] = [];
  selectedDocument: Document | null = null;

  currentModule = '';
  currentProjectId = '';
  currentPage = 1;
  totalPages = 1;
  documentsPerPage = 6;
  currentSortOrder: 'asc' | 'desc' = 'desc';
  isLoading = false;
  limit = 8;
  totalCount = 0;
  Math = Math;

  private destroy$ = new Subject<void>();

  ngOnInit() {
    // Get module name from parent route
    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentModule = params.get('module') || '';
    });

    // Watch route changes (single or list)
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const documentId = params.get('id');
      if (documentId) {
        this.loadDocumentById(documentId);
      } else {
        this.loadProjectIdAndDocuments(this.currentModule);
      }
    });
  }

  // ----------------- Load Project and Initialize Stream -----------------
  loadProjectIdAndDocuments(moduleSlug: string) {
    this.documents = [];
    this.selectedDocument = null;

    this.api.get<any[]>('/projects').pipe(takeUntil(this.destroy$)).subscribe({
      next: projects => {
        const matched = projects.find(p => this.slugify(p.name) === moduleSlug);
        if (matched) {
          this.currentProjectId = matched._id;
          this.setupDocumentStream();
        } else {
          console.warn('No project found for slug:', moduleSlug);
        }
      },
      error: err => console.error('Failed to fetch projects', err),
    });
  }

  // ----------------- Reactive CombineLatest Stream -----------------
  private setupDocumentStream() {
    const user = JSON.parse(localStorage.getItem('userData') || '{}');

    combineLatest([
      this.searchService.search$.pipe(debounceTime(500), startWith('')),
      this.searchService.moduleFilter$.pipe(startWith('all')),
      this.searchService.sortOrder$.pipe(startWith(this.currentSortOrder)),
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([searchTerm, moduleId, sortOrder]) => {
          if (!this.currentProjectId) return of({ documents: [], totalCount: 0 });

          const search = searchTerm?.trim() || undefined;
          const projectModule = moduleId && moduleId !== 'all' ? moduleId : undefined;
          const sort = (sortOrder as 'asc' | 'desc') || 'desc';
          const offset = (this.currentPage - 1) * this.limit;

          this.isLoading = true;

          return this.documentsService.getDocumentsByProject(
            this.currentProjectId,
            user.user_id,
            search,
            projectModule,
            sort,
            this.limit,
            offset
          );
        }),
        map((res) => ({
          documents: res.documents.map((d: any) => ({
            _id: d._id,
            title: d.title,
            description: d.description,
            module: d.projectModule?.moduleName || '',
            thumbnailUrl: d.thumbnailUrl,
            favoritedByCurrentUser: d.favoritedByCurrentUser || false,
            projectId: d.project?._id || this.currentProjectId,
            updatedAt: d.updatedAt ? new Date(d.updatedAt).toLocaleDateString() : '',
          })),
          totalCount: res.totalCount,
        }))
      )
      .subscribe({
        next: (res) => {
          this.documents = res.documents;
          this.totalCount = res.totalCount;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load documents', err);
          this.isLoading = false;
        },
      });
  }

  // ----------------- Single Document -----------------
  loadDocumentById(documentId: string) {
    this.documentsService.getDocumentById(documentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.selectedDocument = {
          _id: res._id,
          title: res.title,
          description: res.description,
          thumbnailUrl: res.thumbnailUrl,
          docData: res.docData,
          projectId: res.project?._id,
          updatedAt: res.updatedAt ? new Date(res.updatedAt).toLocaleDateString() : '',
        };
      },
      error: err => console.error('Failed to load document', err),
    });
  }

  // ----------------- Favorite Toggle -----------------
  toggleFavorite(document: any) {
    const user = JSON.parse(localStorage.getItem('userData') || '{}');
    this.documentsService.toggleFavorite(document._id, user.user_id).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => (document.favoritedByCurrentUser = res.favorited),
      error: err => console.error('Error toggling favorite', err),
    });
  }

  // ----------------- Pagination -----------------
  get paginatedDocuments() {
    const startIndex = (this.currentPage - 1) * this.documentsPerPage;
    return this.documents.slice(startIndex, startIndex + this.documentsPerPage);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.setupDocumentStream();
  }

  onLimitChange(newLimit: number) {
    this.limit = newLimit;
    this.currentPage = 1;
    this.setupDocumentStream();
  }

  // ----------------- Navigation -----------------
  viewDocument(document: Document) {
    this.selectedDocument = document;
    this.documentViewStateChange.emit(true);
    this.router.navigate(['../document', document._id], { relativeTo: this.route });
  }

  clearSelection() {
    this.selectedDocument = null;
    this.documentViewStateChange.emit(false);
    this.router.navigate(['/content', this.currentModule, 'documents']);
  }

  // ----------------- Utility -----------------
  slugify(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
